/**
 * Shared read model for a persona's sendable-asset grants (SA-47).
 *
 * One React Query cache for the asset library + per-persona grants so the
 * workshop's Sendable-assets card, the Capabilities simple-mode status row,
 * and the legacy editor section all agree. Writers must invalidate
 * `personaAssetGrantsQueryKey(personaId)` (and `activeAssetsQueryKey` if the
 * library changed) after `setPersonaAssetGrants`.
 */
import { useQuery } from '@tanstack/react-query'
import { listAssets, getPersonaAssetGrants } from '@/services/assets'
import type { ApprovedAsset } from '@/types/assets'

export const activeAssetsQueryKey = ['assets', 'library'] as const

export function personaAssetGrantsQueryKey(personaId: string) {
  return ['persona-asset-grants', personaId] as const
}

export function usePersonaAssetGrants(personaId: string) {
  const assetsQ = useQuery({
    queryKey: activeAssetsQueryKey,
    queryFn: listAssets,
    staleTime: 60_000,
  })
  const grantsQ = useQuery({
    queryKey: personaAssetGrantsQueryKey(personaId),
    queryFn: () => getPersonaAssetGrants(personaId),
    enabled: !!personaId,
    staleTime: 60_000,
  })

  const activeAssets: ApprovedAsset[] = (assetsQ.data ?? []).filter((a) => a.isActive)
  const grantAll = grantsQ.data?.grantAll ?? false
  const grantedIds = grantsQ.data?.assetIds ?? []
  const grantedSet = new Set(grantedIds)
  const sendableCount = grantAll
    ? activeAssets.length
    : activeAssets.filter((a) => grantedSet.has(a.id)).length

  return {
    activeAssets,
    grantAll,
    grantedIds,
    sendableCount,
    isLoading: assetsQ.isLoading || grantsQ.isLoading,
    // Stable number that changes only when the grants query's data actually
    // updates (initial load, sibling-mount-triggered invalidation refetch,
    // or an explicit refetch) — unlike `grantedIds`, which is a fresh array
    // reference every render. Consumers that need to reseed local state
    // from the server should key an effect on this, not on `grantedIds`.
    dataUpdatedAt: grantsQ.dataUpdatedAt,
  }
}
